import { 
  collection, 
  addDoc, 
  getDocs, 
  doc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp,
  increment
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Referral service
export const referralService = {
  // Create referral code for user
  async createReferralCode(userId, userData) {
    try {
      const referralCode = this.generateReferralCode(userData.firstName, userData.lastName);
      
      const referralData = {
        userId,
        referralCode,
        totalReferrals: 0,
        successfulReferrals: 0,
        totalEarnings: 0,
        pendingEarnings: 0,
        createdAt: serverTimestamp(),
        isActive: true
      };
      
      const docRef = await addDoc(collection(db, 'referrals'), referralData);
      return { id: docRef.id, ...referralData };
    } catch (error) {
      console.error('Error creating referral code:', error);
      throw error;
    }
  },

  // Generate unique referral code
  generateReferralCode(firstName, lastName) {
    // Handle undefined/null values and ensure they're strings
    const first = (firstName || 'USER').toString();
    const last = (lastName || 'REF').toString();
    const prefix = (first + last).toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    return `${prefix}${random}`;
  },

  // Get referral data by user ID
  async getReferralData(userId) {
    try {
      const q = query(
        collection(db, 'referrals'),
        where('userId', '==', userId)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error('Error fetching referral data:', error);
      throw error;
    }
  },

  // Get referral data by referral code
  async getReferralByCode(referralCode) {
    try {
      const q = query(
        collection(db, 'referrals'),
        where('referralCode', '==', referralCode)
      );
      
      const querySnapshot = await getDocs(q);
      if (querySnapshot.empty) {
        return null;
      }
      
      const doc = querySnapshot.docs[0];
      return { id: doc.id, ...doc.data() };
    } catch (error) {
      console.error('Error fetching referral by code:', error);
      throw error;
    }
  },

  // Process new referral
  async processReferral(referralCode, newUserId, newUserData) {
    try {
      // Get referrer data
      const referrerData = await this.getReferralByCode(referralCode);
      if (!referrerData) {
        throw new Error('Invalid referral code');
      }

      // Create referral record
      const referralRecord = {
        referrerId: referrerData.userId,
        referredUserId: newUserId,
        referralCode,
        status: 'pending', // pending, completed, expired
        rewardAmount: 25,
        createdAt: serverTimestamp(),
        newUserData: {
          firstName: newUserData.firstName,
          lastName: newUserData.lastName,
          email: newUserData.email
        }
      };

      const docRef = await addDoc(collection(db, 'referralRecords'), referralRecord);

      // Update referrer's pending earnings
      await updateDoc(doc(db, 'referrals', referrerData.id), {
        totalReferrals: increment(1),
        pendingEarnings: increment(25)
      });

      return { id: docRef.id, ...referralRecord };
    } catch (error) {
      console.error('Error processing referral:', error);
      throw error;
    }
  },

  // Complete referral (when referred user makes first purchase)
  async completeReferral(referralRecordId) {
    try {
      // Update referral record
      await updateDoc(doc(db, 'referralRecords', referralRecordId), {
        status: 'completed',
        completedAt: serverTimestamp()
      });

      // Get referral record to find referrer
      const referralRecord = await this.getReferralRecord(referralRecordId);
      if (!referralRecord) {
        throw new Error('Referral record not found');
      }

      // Update referrer's successful referrals and earnings
      const referrerData = await this.getReferralByCode(referralRecord.referralCode);
      if (referrerData) {
        await updateDoc(doc(db, 'referrals', referrerData.id), {
          successfulReferrals: increment(1),
          totalEarnings: increment(25),
          pendingEarnings: increment(-25)
        });
      }

      return { success: true };
    } catch (error) {
      console.error('Error completing referral:', error);
      throw error;
    }
  },

  // Get referral record by ID
  async getReferralRecord(referralRecordId) {
    try {
      const docRef = doc(db, 'referralRecords', referralRecordId);
      const docSnap = await getDocs(docRef);
      
      if (docSnap.exists()) {
        return { id: docSnap.id, ...docSnap.data() };
      }
      return null;
    } catch (error) {
      console.error('Error fetching referral record:', error);
      throw error;
    }
  },

  // Get user's referral history
  async getUserReferralHistory(userId) {
    try {
      const q = query(
        collection(db, 'referralRecords'),
        where('referrerId', '==', userId),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching referral history:', error);
      throw error;
    }
  },

  // Get pending referrals for a user
  async getPendingReferrals(userId) {
    try {
      const q = query(
        collection(db, 'referralRecords'),
        where('referrerId', '==', userId),
        where('status', '==', 'pending')
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching pending referrals:', error);
      throw error;
    }
  }
};

// Referral analytics service
export const referralAnalyticsService = {
  // Get referral statistics
  async getReferralStats() {
    try {
      const referralsQuery = query(collection(db, 'referrals'));
      const referralsSnapshot = await getDocs(referralsQuery);
      
      const referralRecordsQuery = query(collection(db, 'referralRecords'));
      const referralRecordsSnapshot = await getDocs(referralRecordsQuery);
      
      const referrals = referralsSnapshot.docs.map(doc => doc.data());
      const referralRecords = referralRecordsSnapshot.docs.map(doc => doc.data());
      
      const stats = {
        totalReferrers: referrals.length,
        totalReferrals: referralRecords.length,
        successfulReferrals: referralRecords.filter(r => r.status === 'completed').length,
        pendingReferrals: referralRecords.filter(r => r.status === 'pending').length,
        totalRewardsPaid: referrals.reduce((sum, r) => sum + (r.totalEarnings || 0), 0),
        pendingRewards: referrals.reduce((sum, r) => sum + (r.pendingEarnings || 0), 0),
        averageReferralsPerUser: referrals.length > 0 ? referralRecords.length / referrals.length : 0
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching referral stats:', error);
      throw error;
    }
  },

  // Get top referrers
  async getTopReferrers(limitCount = 10) {
    try {
      const q = query(
        collection(db, 'referrals'),
        orderBy('successfulReferrals', 'desc'),
        limit(limitCount)
      );
      
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching top referrers:', error);
      throw error;
    }
  }
};
